import React, { memo } from 'react';
import { NodeProps } from '@xyflow/react';
import { useWorkflowStore } from './store';
import { Users, Database, FileText } from 'lucide-react';

// ── Data Zone Constants ─────────────────────────────────────────────────────
export const DATA_ZONE_ID = 'data-zone';
export const DATA_ZONE_POSITION = { x: -50, y: -280 };
export const DATA_ZONE_WIDTH = 540;
export const DATA_ZONE_HEIGHT = 180;

export const DATA_NODE_TYPES = ['account_selector', 'brand_info', 'seeding_contents_manager'];

export const ZONE_SLOTS = [
  { type: 'account_selector', label: 'Accounts', shape: 'hexagon' as const, icon: Users, x: 40, y: 55 },
  { type: 'brand_info', label: 'Brand', shape: 'diamond' as const, icon: Database, x: 220, y: 55 },
  { type: 'seeding_contents_manager', label: 'Contents', shape: 'circle' as const, icon: FileText, x: 400, y: 55 },
];

// ── Slot shape renderers ────────────────────────────────────────────────────
const HexagonSlot: React.FC<{ filled: boolean; icon: any; label: string }> = ({ filled, icon: Icon, label }) => (
  <div className="flex flex-col items-center gap-1.5">
    <div className="relative w-[100px] h-[90px]">
      <svg viewBox="0 0 100 90" className="w-full h-full">
        <polygon
          points="50,3 95,25 95,65 50,87 5,65 5,25"
          fill={filled ? 'transparent' : '#fefce8'}
          stroke="#e5e7eb"
          strokeWidth="2"
          strokeDasharray={filled ? '0' : '8 4'}
          opacity={filled ? 0.3 : 0.8}
        />
      </svg>
      {!filled && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon size={22} className="text-gray-300" />
        </div>
      )}
    </div>
    {!filled && <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</span>}
  </div>
);

const DiamondSlot: React.FC<{ filled: boolean; icon: any; label: string }> = ({ filled, icon: Icon, label }) => (
  <div className="flex flex-col items-center gap-1.5">
    <div className="relative w-[100px] h-[90px]">
      <svg viewBox="0 0 100 90" className="w-full h-full">
        <rect
          x="18" y="13" width="64" height="64" rx="10"
          fill={filled ? 'transparent' : '#eff6ff'}
          stroke="#e5e7eb"
          strokeWidth="2"
          strokeDasharray={filled ? '0' : '8 4'}
          opacity={filled ? 0.3 : 0.8}
          transform="rotate(45 50 45)"
        />
      </svg>
      {!filled && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon size={22} className="text-gray-300" />
        </div>
      )}
    </div>
    {!filled && <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</span>}
  </div>
);

const CircleSlot: React.FC<{ filled: boolean; icon: any; label: string }> = ({ filled, icon: Icon, label }) => (
  <div className="flex flex-col items-center gap-1.5">
    <div className="relative w-[100px] h-[90px]">
      <svg viewBox="0 0 100 90" className="w-full h-full">
        <circle
          cx="50" cy="45" r="40"
          fill={filled ? 'transparent' : '#ecfdf5'}
          stroke="#e5e7eb"
          strokeWidth="2"
          strokeDasharray={filled ? '0' : '8 4'}
          opacity={filled ? 0.3 : 0.8}
        />
      </svg>
      {!filled && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon size={22} className="text-gray-300" />
        </div>
      )}
    </div>
    {!filled && <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</span>}
  </div>
);

const SHAPE_COMPONENTS = {
  hexagon: HexagonSlot,
  diamond: DiamondSlot,
  circle: CircleSlot,
};

// ── DataZone Node ───────────────────────────────────────────────────────────
const DataZoneNode: React.FC<NodeProps> = () => {
  const nodes = useWorkflowStore((s) => s.nodes);
  const filledTypes = new Set(
    nodes.filter((n) => DATA_NODE_TYPES.includes(n.type || '')).map((n) => n.type)
  );

  return (
    <div
      data-tour="data-zone"
      className="rounded-2xl border-2 border-dashed border-gray-200/80 bg-white/40 backdrop-blur-sm"
      style={{ width: DATA_ZONE_WIDTH, height: DATA_ZONE_HEIGHT }}
    >
      {/* Title */}
      <div className="px-5 pt-3 pb-1">
        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.15em]">
          Data Injection
        </span>
      </div>

      {/* Slots */}
      <div className="flex items-start justify-around px-5 pt-1">
        {ZONE_SLOTS.map((slot) => {
          const ShapeComp = SHAPE_COMPONENTS[slot.shape];
          return (
            <ShapeComp
              key={slot.type}
              filled={filledTypes.has(slot.type)}
              icon={slot.icon}
              label={slot.label}
            />
          );
        })}
      </div>
    </div>
  );
};

export default memo(DataZoneNode);
